<template>
  <div class="cost-table-page partner-tab-section">
    <div class="refined-header">
      <div>
        <h2 class="refined-title">Maliyet Tablosu</h2>
        <p class="refined-subtitle">Seçili pazaryeri için ürün formül çarpanları (ilk iki çarpan)</p>
      </div>
      <div class="refined-header-actions">
        <button type="button" class="refined-primary-btn" :disabled="loading" @click="load">
          <v-icon small class="mr-1">refresh</v-icon>
          Yenile
        </button>
        <button
          type="button"
          class="refined-primary-btn refined-btn-success"
          :class="{ 'refined-btn-success-emphasis': selected.length > 1 }"
          :disabled="!selectedEditable.length || loading"
          @click="openDialogFromSelection"
        >
          Çarpanı düzenle ({{ selectedEditable.length }})
        </button>
      </div>
    </div>

    <div class="refined-filters">
      <div class="refined-filter-item">
        <label class="refined-filter-label">Ara</label>
        <input
          v-model="searchInput"
          type="text"
          class="refined-input"
          placeholder="Ürün adı, ana SKU veya MP SKU"
          @keyup.enter="load"
        />
      </div>
      <div class="refined-filter-item">
        <label class="refined-filter-label">Ana kategori</label>
        <select v-model.number="categoryId" class="refined-select" @change="onCategoryChange">
          <option v-for="c in categorySelectItems" :key="'cat-' + c.id" :value="c.id">
            {{ c.name }}
          </option>
        </select>
      </div>
      <div class="refined-filter-item refined-filter-submit">
        <label class="refined-filter-label">&nbsp;</label>
        <button type="button" class="refined-primary-btn" :disabled="loading" @click="load">Ara</button>
      </div>
    </div>

    <div class="refined-table-wrap">
      <div class="refined-table-scroll">
        <table class="refined-table cost-refined-table">
          <thead>
            <tr>
              <th class="refined-th-check">
                <v-checkbox
                  :input-value="headerCheckboxAll"
                  :indeterminate="headerCheckboxIndeterminate"
                  primary
                  hide-details
                  class="mt-0 pt-0"
                  @click.stop.prevent="toggleAll"
                ></v-checkbox>
              </th>
              <th class="refined-th-sort" @click="changeSort('productName')">
                Ürün adı
                <v-icon v-if="sortBy === 'productName'" small class="refined-sort-icon">{{
                  sortDesc ? "arrow_downward" : "arrow_upward"
                }}</v-icon>
              </th>
              <th class="refined-th-sort" @click="changeSort('productSku')">
                Ana SKU
                <v-icon v-if="sortBy === 'productSku'" small class="refined-sort-icon">{{
                  sortDesc ? "arrow_downward" : "arrow_upward"
                }}</v-icon>
              </th>
              <th class="refined-th-sort" @click="changeSort('mpSku')">
                MP SKU
                <v-icon v-if="sortBy === 'mpSku'" small class="refined-sort-icon">{{
                  sortDesc ? "arrow_downward" : "arrow_upward"
                }}</v-icon>
              </th>
              <th class="refined-th-sort" @click="changeSort('categoryName')">
                Kategori
                <v-icon v-if="sortBy === 'categoryName'" small class="refined-sort-icon">{{
                  sortDesc ? "arrow_downward" : "arrow_upward"
                }}</v-icon>
              </th>
              <th class="refined-th-sort refined-th-num" @click="changeSort('multiplier')">
                Çarpan (1.×2.)
                <v-icon v-if="sortBy === 'multiplier'" small class="refined-sort-icon">{{
                  sortDesc ? "arrow_downward" : "arrow_upward"
                }}</v-icon>
              </th>
              <th>Formül</th>
              <th class="refined-th-actions">Düzenle</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="8" class="refined-loading">
                <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                Yükleniyor...
              </td>
            </tr>
            <tr v-else-if="!paginatedItems.length">
              <td colspan="8" class="refined-empty">Kayıt yok</td>
            </tr>
            <tr
              v-for="item in paginatedItems"
              v-else
              :key="item.mpProductId"
              class="refined-row"
              :class="{ 'cost-row-selected': isRowSelected(item) }"
              @click="onRowClick(item)"
            >
              <td class="refined-td-check" @click.stop>
                <v-checkbox
                  :input-value="isRowSelected(item)"
                  :disabled="!item.canEditMultiplier"
                  primary
                  hide-details
                  class="mt-0 pt-0"
                  @click.stop.prevent="toggleRowSelection(item)"
                ></v-checkbox>
              </td>
              <td class="refined-cell-bold">{{ item.productName }}</td>
              <td class="cost-sku-cell" :title="item.productSku || ''">{{ item.productSku || "—" }}</td>
              <td class="cost-sku-cell" :title="item.mpSku || ''">{{ item.mpSku || "—" }}</td>
              <td>{{ item.categoryName || "—" }}</td>
              <td class="refined-th-num">
                <span v-if="item.multiplier != null" class="refined-cell-value">{{
                  formatMultiplier(item.multiplier)
                }}</span>
                <span v-else class="refined-muted">—</span>
              </td>
              <td
                class="cost-formula-cell refined-muted"
                :title="item.formula || ''"
              >
                {{ item.formula || "—" }}
              </td>
              <td class="refined-td-actions" @click.stop>
                <button
                  type="button"
                  class="refined-action-btn"
                  :class="{ 'refined-action-btn-muted': selected.length > 1 && item.canEditMultiplier }"
                  :disabled="!item.canEditMultiplier || selected.length > 1"
                  :title="
                    selected.length > 1 && item.canEditMultiplier
                      ? 'Çoklu seçimde üstteki «Çarpanı düzenle» kullanın'
                      : 'Düzenle'
                  "
                  @click="openDialogForRow(item)"
                >
                  Düzenle
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="refined-table-footer">
        <div class="refined-footer-left">
          <span>Sayfa başına satır:</span>
          <select v-model.number="rowsPerPage" class="refined-rows-select" @change="page = 1">
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
            <option :value="200">200</option>
          </select>
        </div>
        <span class="refined-pagination-info">{{ paginationInfo }}</span>
        <div class="refined-footer-actions">
          <button
            type="button"
            class="refined-page-btn"
            :disabled="page <= 1"
            @click="page = Math.max(1, page - 1)"
          >
            <v-icon small>chevron_left</v-icon>
          </button>
          <button
            type="button"
            class="refined-page-btn"
            :disabled="page >= totalPages"
            @click="page = Math.min(totalPages, page + 1)"
          >
            <v-icon small>chevron_right</v-icon>
          </button>
        </div>
      </div>
    </div>

    <v-dialog v-model="dialog" max-width="420px" persistent>
      <v-card>
        <v-card-title class="headline">Çarpan (ilk iki çarpanın çarpımı)</v-card-title>
        <v-card-text>
          <p class="body-1 mb-2">
            Seçili ürün sayısı: <strong>{{ dialogRows.length }}</strong>
          </p>
          <p class="caption mb-3">
            Hedef çarpımı girin; her ürün için formülün <strong>ikinci</strong> çarpanı, birinci çarpan sabit
            kalarak bu değeri verecek şekilde güncellenir. Üçüncü ve sonraki çarpanlar aynı kalır.
          </p>
          <v-text-field
            v-model="targetMultiplierInput"
            label="Hedef çarpım"
            placeholder="Örn. 2,1"
            type="text"
            hide-details
            box
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="dialog = false">İptal</v-btn>
          <v-btn color="primary" flat :loading="saving" @click="saveMultiplier">Kaydet</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="5000" top>
      {{ snackbar.text }}
      <v-btn flat @click="snackbar.show = false">Kapat</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import costTableApi from "@/api/costTable";

export default {
  name: "CostTableIndex",
  data() {
    return {
      items: [],
      selected: [],
      searchInput: "",
      categoryId: 0,
      loading: false,
      page: 1,
      rowsPerPage: 25,
      sortBy: "productName",
      sortDesc: false,
      dialog: false,
      dialogRows: [],
      targetMultiplierInput: "",
      saving: false,
      snackbar: { show: false, text: "", color: "success" },
    };
  },
  computed: {
    categorySelectItems() {
      const all = this.$store.state.productCategory.all || [];
      return [{ id: 0, name: "Tümü" }].concat(
        all.map((c) => ({ id: c.id, name: c.name || `Kategori #${c.id}` }))
      );
    },
    selectedEditable() {
      return (this.selected || []).filter((r) => r.canEditMultiplier);
    },
    editableItems() {
      return this.items.filter((i) => i.canEditMultiplier);
    },
    sortedItems() {
      const list = [...this.items];
      const key = this.sortBy;
      const desc = this.sortDesc;
      list.sort((a, b) => {
        let va;
        let vb;
        if (key === "multiplier") {
          va = a.multiplier != null ? Number(a.multiplier) : null;
          vb = b.multiplier != null ? Number(b.multiplier) : null;
          if (va == null && vb == null) return 0;
          if (va == null) return 1;
          if (vb == null) return -1;
        } else {
          va = (a[key] != null ? String(a[key]) : "").toLowerCase();
          vb = (b[key] != null ? String(b[key]) : "").toLowerCase();
        }
        let c = 0;
        if (va < vb) c = -1;
        else if (va > vb) c = 1;
        return desc ? -c : c;
      });
      return list;
    },
    totalPages() {
      const n = this.sortedItems.length;
      if (n === 0) return 1;
      return Math.max(1, Math.ceil(n / this.rowsPerPage));
    },
    paginatedItems() {
      const start = (this.page - 1) * this.rowsPerPage;
      return this.sortedItems.slice(start, start + this.rowsPerPage);
    },
    paginationInfo() {
      const total = this.sortedItems.length;
      if (total === 0) return "0 kayıt";
      const start = (this.page - 1) * this.rowsPerPage + 1;
      const end = Math.min(this.page * this.rowsPerPage, total);
      return `${start}–${end} / ${total} kayıt`;
    },
    headerCheckboxAll() {
      const ed = this.editableItems;
      if (!ed.length) return false;
      return ed.every((i) => this.isRowSelected(i));
    },
    headerCheckboxIndeterminate() {
      const ed = this.editableItems;
      if (!ed.length) return false;
      const any = ed.some((i) => this.isRowSelected(i));
      return any && !this.headerCheckboxAll;
    },
  },
  watch: {
    page() {
      const max = this.totalPages;
      if (this.page > max) this.page = max;
    },
    totalPages(max) {
      if (this.page > max) this.page = max;
    },
  },
  async mounted() {
    await this.$store.dispatch("productCategory/getAllProductCategories");
    await this.load();
  },
  methods: {
    onCategoryChange() {
      this.page = 1;
      this.load();
    },
    formatMultiplier(v) {
      if (v == null) return "—";
      const n = Number(v);
      if (Number.isNaN(n)) return String(v);
      return n.toLocaleString("tr-TR", { maximumFractionDigits: 8 });
    },
    parseTargetMultiplier() {
      const s = (this.targetMultiplierInput || "").trim().replace(",", ".");
      const n = parseFloat(s);
      if (Number.isNaN(n)) return null;
      return n;
    },
    isRowSelected(item) {
      return this.selected.some((s) => s.mpProductId === item.mpProductId);
    },
    async load() {
      this.loading = true;
      try {
        const catId = this.categoryId > 0 ? this.categoryId : null;
        const res = await costTableApi.getCostTable(this.searchInput || null, catId);
        this.items = res.data || [];
        this.selected = [];
        this.page = 1;
      } catch (e) {
        this.showSnack("Liste yüklenemedi.", "error");
      } finally {
        this.loading = false;
      }
    },
    toggleAll() {
      const ed = this.editableItems;
      if (!ed.length) return;
      if (this.headerCheckboxAll) {
        const removeIds = new Set(ed.map((i) => i.mpProductId));
        this.selected = this.selected.filter((s) => !removeIds.has(s.mpProductId));
      } else {
        const map = new Map(this.selected.map((s) => [s.mpProductId, s]));
        ed.forEach((i) => map.set(i.mpProductId, i));
        this.selected = Array.from(map.values());
      }
    },
    onRowClick(item) {
      if (!item.canEditMultiplier) return;
      this.toggleRowSelection(item);
    },
    toggleRowSelection(item) {
      if (!item.canEditMultiplier) return;
      const idx = this.selected.findIndex((s) => s.mpProductId === item.mpProductId);
      if (idx >= 0) this.selected.splice(idx, 1);
      else this.selected.push(item);
    },
    changeSort(column) {
      if (this.sortBy === column) this.sortDesc = !this.sortDesc;
      else {
        this.sortBy = column;
        this.sortDesc = false;
      }
    },
    openDialogFromSelection() {
      const rows = this.selectedEditable;
      if (!rows.length) {
        this.showSnack("Düzenlenebilir satır seçin (geçerli formül gerekir).", "warning");
        return;
      }
      this.openDialog(rows);
    },
    openDialogForRow(item) {
      if (!item.canEditMultiplier) return;
      this.selected = [item];
      this.openDialog([item]);
    },
    openDialog(rows) {
      this.dialogRows = rows.slice();
      if (rows.length === 1) {
        const first = rows[0];
        if (first && first.multiplier != null)
          this.targetMultiplierInput = String(first.multiplier).replace(".", ",");
        else this.targetMultiplierInput = "";
      } else {
        this.targetMultiplierInput = "";
      }
      this.dialog = true;
    },
    async saveMultiplier() {
      const target = this.parseTargetMultiplier();
      if (target == null) {
        this.showSnack("Geçerli bir hedef çarpım girin.", "warning");
        return;
      }
      this.saving = true;
      try {
        const body = {
          mpProductIds: this.dialogRows.map((r) => r.mpProductId),
          targetMultiplier: target,
        };
        const res = await costTableApi.updateMultiplier(body);
        const results = (res.data && res.data.results) || [];
        const failed = results.filter((r) => !r.success);
        if (failed.length) {
          const msg = failed.map((f) => `#${f.mpProductId}: ${f.message || "Hata"}`).join("; ");
          this.showSnack(`Bazı satırlar güncellenemedi. ${msg}`, "error");
        } else {
          this.showSnack("Formüller güncellendi.", "success");
        }
        this.dialog = false;
        await this.load();
      } catch (e) {
        const msg =
          (e.response && e.response.data && e.response.data.message) || e.message || "Kayıt başarısız.";
        this.showSnack(msg, "error");
      } finally {
        this.saving = false;
      }
    },
    showSnack(text, color) {
      this.snackbar = { show: true, text, color: color || "success" };
    },
  },
};
</script>

<style scoped>
.partner-tab-section {
  max-width: 80rem;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
}

.refined-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.refined-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.025em;
}

.refined-subtitle {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0.25rem 0 0 0;
}

.refined-primary-btn {
  display: inline-flex;
  align-items: center;
  background: #2563eb;
  color: #fff;
  padding: 0.625rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  text-decoration: none;
  box-shadow: 0 1px 2px rgba(37, 99, 235, 0.2);
  transition: background 0.2s, box-shadow 0.2s;
}

.refined-primary-btn:hover:not(:disabled) {
  background: #1d4ed8;
}

.refined-primary-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.refined-btn-success {
  background: #059669;
  box-shadow: 0 1px 2px rgba(5, 150, 105, 0.2);
}

.refined-btn-success:hover:not(:disabled) {
  background: #047857;
}

.refined-btn-success-emphasis {
  box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.35), 0 4px 14px rgba(5, 150, 105, 0.35) !important;
  transform: scale(1.02);
}

.refined-header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.refined-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: flex-end;
}

.refined-filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.refined-filter-submit .refined-primary-btn {
  margin-top: 1.25rem;
}

.refined-filter-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.refined-input,
.refined-select {
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  font-size: 0.875rem;
  color: #0f172a;
  min-width: 200px;
  background: #fff;
}

.refined-input:focus,
.refined-select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
}

.refined-table-wrap {
  background: #fff;
  border-radius: 0.625rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.refined-table-scroll {
  overflow-x: auto;
}

.refined-table {
  width: 100%;
  text-align: left;
  border-collapse: collapse;
}

.refined-table thead tr {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.refined-table th {
  padding: 1rem 1.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  vertical-align: middle;
}

.refined-th-check {
  width: 52px;
  text-align: center;
  padding-left: 1rem !important;
  padding-right: 0.5rem !important;
}

.refined-th-sort {
  cursor: pointer;
  user-select: none;
}

.refined-th-sort:hover {
  color: #2563eb;
}

.refined-sort-icon {
  vertical-align: middle;
  margin-left: 2px;
}

.refined-th-num {
  text-align: right;
}

.refined-th-actions {
  text-align: center;
  width: 120px;
}

.refined-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s;
  cursor: default;
}

.refined-table tbody tr:hover {
  background: rgba(248, 250, 252, 0.7);
}

.cost-row-selected {
  background: rgba(37, 99, 235, 0.2) !important;
  box-shadow: inset 4px 0 0 0 #1d4ed8;
}

.cost-row-selected:hover {
  background: rgba(37, 99, 235, 0.28) !important;
}

.refined-table td {
  padding: 1rem 1.5rem;
  font-size: 0.875rem;
  color: #475569;
  vertical-align: middle;
}

.refined-td-check {
  width: 52px;
  text-align: center;
  padding-left: 1rem !important;
  padding-right: 0.5rem !important;
}

.refined-td-actions {
  text-align: center;
}

.refined-cell-bold {
  font-weight: 600;
  color: #1e293b;
}

.refined-cell-value {
  font-weight: 500;
  color: #1e293b;
}

.refined-muted {
  color: #94a3b8;
}

.cost-formula-cell {
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cost-sku-cell {
  font-family: "SF Mono", ui-monospace, monospace;
  font-size: 0.8125rem;
  color: #334155;
  max-width: 160px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.refined-action-btn {
  padding: 0.35rem 0.75rem;
  border-radius: 0.375rem;
  border: none;
  background: #2563eb;
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.refined-action-btn:hover:not(:disabled) {
  background: #1d4ed8;
}

.refined-action-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.refined-action-btn.refined-action-btn-muted:disabled {
  background: #cbd5e1 !important;
  color: #64748b !important;
  box-shadow: none !important;
  opacity: 0.85;
  cursor: not-allowed;
  pointer-events: none;
}

.refined-loading,
.refined-empty {
  text-align: center;
  padding: 2rem !important;
  color: #64748b;
}

.refined-table-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1.5rem;
  padding: 1rem 1.5rem;
  background: rgba(248, 250, 252, 0.6);
  font-size: 0.875rem;
  color: #64748b;
  flex-wrap: wrap;
}

.refined-footer-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.refined-rows-select {
  background: transparent;
  border: none;
  color: #0f172a;
  cursor: pointer;
  font-weight: 500;
  padding: 0.25rem 0;
}

.refined-rows-select:focus {
  outline: none;
}

.refined-pagination-info {
  margin-left: auto;
}

.refined-footer-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.refined-page-btn {
  padding: 0.25rem;
  border: none;
  background: transparent;
  border-radius: 0.25rem;
  cursor: pointer;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.refined-page-btn:hover:not(:disabled) {
  background: #e2e8f0;
  color: #0f172a;
}

.refined-page-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.cost-refined-table >>> .v-input--selection-controls {
  margin-top: 0 !important;
  padding-top: 0 !important;
}
</style>
